const BUILD_ENVIRONMENT = "build";

const BUILDER_SECTIONS = new Set([
  "branding",
  "components",
  "dev",
  "features",
  "pages",
  "settings",
  "stories",
  "support",
  "users",
  "dashboard",
  "task",
]);

const DEFAULT_SECTION_URI = {
  component: "/components",
  page: "/pages",
  story: "/stories/stories",
  dashboard: "/dashboard",
};

function isBuildEnvironment() {
  return typeof window !== "undefined" && window.blclient?.data?.environment === BUILD_ENVIRONMENT;
}

export function getRouteParts(uri) {
  const parts = (uri || "/").split("/").filter(Boolean);
  const section = parts[0] || null;
  let page = parts[1] || null;
  let subPage = parts.slice(2).join("/") || null;

  if (section === "users") {
    if (!page) {
      page = "users";
    } else if (["applications", "invites", "invite"].includes(page)) {
      subPage = [page, ...parts.slice(2)].join("/") || null;
      page = "users";
    }
  }

  return {
    section,
    page,
    subPage,
  };
}

function readPathname(value = null) {
  if (value instanceof URL) {
    return value.pathname;
  }

  if (typeof value === "string") {
    if (!value) {
      return "/";
    }

    if (value.startsWith("/")) {
      return value;
    }

    return new URL(value).pathname;
  }

  if (typeof value?.href === "string") {
    return new URL(value.href).pathname;
  }

  if (typeof window !== "undefined") {
    return window.location.pathname;
  }

  return "/";
}

function readUrl(value = null) {
  if (value instanceof URL) {
    return new URL(value.toString());
  }

  if (typeof value === "string") {
    if (!value || value.startsWith("/")) {
      return null;
    }

    return new URL(value);
  }

  if (typeof value?.href === "string") {
    return new URL(value.href);
  }

  if (typeof window !== "undefined") {
    return new URL(window.location.href);
  }

  return null;
}

function getPathSegments(pathname) {
  return (pathname || "/").split("/").filter(Boolean);
}

function getPathFromSegments(segments) {
  return segments.length ? `/${segments.join("/")}` : "/";
}

function getUriFromSegments(segments) {
  return segments.length ? `/${segments.join("/")}` : "/";
}

function getRouteUri(uriSegments, fallbackUri = "/") {
  if (!uriSegments.length) {
    return fallbackUri;
  }

  const [section] = uriSegments;

  if (!BUILDER_SECTIONS.has(section)) {
    return fallbackUri;
  }

  return getUriFromSegments(uriSegments);
}

function getEmptyContext(pathname) {
  return {
    path: pathname || "/",
    entityId: null,
    kind: "none",
    rootPath: pathname || "/",
    uriSegments: [],
  };
}

function getContextFromSegments(segments, startIndex, rootPath, allowEmptyRoot = false) {
  if (segments[startIndex] === "page" && segments[startIndex + 1]) {
    return {
      path: getPathFromSegments(segments.slice(0, startIndex + 2)),
      entityId: segments[startIndex + 1],
      kind: "page",
      rootPath,
      uriSegments: segments.slice(startIndex + 2),
    };
  }

  if (segments[startIndex] === "dynamic-component" && segments[startIndex + 1]) {
    return {
      path: getPathFromSegments(segments.slice(0, startIndex + 2)),
      entityId: segments[startIndex + 1],
      kind: "component",
      rootPath,
      uriSegments: segments.slice(startIndex + 2),
    };
  }

  if (segments[startIndex] === "story" && segments[startIndex + 1] && segments[startIndex + 2] === "edit") {
    return {
      path: getPathFromSegments(segments.slice(0, startIndex + 3)),
      entityId: segments[startIndex + 1],
      kind: "story",
      rootPath,
      uriSegments: segments.slice(startIndex + 3),
    };
  }

  if (BUILDER_SECTIONS.has(segments[startIndex])) {
    return {
      path: getPathFromSegments(segments.slice(0, startIndex + 2)),
      entityId: null,
      kind: "root",
      rootPath,
      uriSegments: segments.slice(startIndex),
    };
  }

  if (allowEmptyRoot && segments.length === startIndex) {
    return {
      path: getPathFromSegments(segments.slice(0, startIndex + 2)),
      entityId: null,
      kind: "root",
      rootPath,
      uriSegments: [],
    };
  }

  return null;
}

function getLocationContext(value = null) {
  const pathname = readPathname(value);
  const segments = getPathSegments(pathname);

  if (!isBuildEnvironment()) {
    return getEmptyContext(pathname);
  }

  if (segments[0] === BUILD_ENVIRONMENT && segments[1]) {
    const rootPath = getPathFromSegments(segments.slice(0, 2));
    const context = getContextFromSegments(segments, 2, rootPath, true) || getEmptyContext(pathname);

    return {
      ...context,
      path: pathname,
    };
  }

  const context = getContextFromSegments(segments, 0, "/", true) || getEmptyContext(pathname);

  return {
    ...context,
    path: pathname,
  };
}

export function getBuilderLocationContext(value = null) {
  const context = getLocationContext(value);

  return {
    path: context.path,
    entityId: context.entityId,
    kind: context.kind,
    rootPath: context.rootPath,
  };
}

export function getRouteUriFromLocation(value = null) {
  const context = getLocationContext(value);

  if (context.kind === "page") {
    return getRouteUri(context.uriSegments, DEFAULT_SECTION_URI.page);
  }

  if (context.kind === "component") {
    return getRouteUri(context.uriSegments, DEFAULT_SECTION_URI.component);
  }

  if (context.kind === "story") {
    return getRouteUri(context.uriSegments, DEFAULT_SECTION_URI.story);
  }

  if (context.kind === "root") {
    return getRouteUri(context.uriSegments, DEFAULT_SECTION_URI.page);
  }

  return "/";
}

function getRoutePath(basePath, uri = "/") {
  const context = getBuilderLocationContext(basePath);
  const cleanBasePath = (context.rootPath || "/").replace(/\/+$/, "") || "/";
  const cleanUri = uri && uri !== "/" ? uri.replace(/^\/+/, "") : "";

  if (!cleanUri) {
    return cleanBasePath;
  }

  if (cleanBasePath === "/") {
    return `/${cleanUri}`;
  }

  return `${cleanBasePath}/${cleanUri}`;
}

export function getBuilderUrl(basePath, uri = "/", value = null) {
  const pathname = getRoutePath(readPathname(basePath), uri);
  const url = readUrl(value);

  if (!url) {
    return pathname;
  }

  url.pathname = pathname;
  url.hash = "";
  return url.toString();
}

export function readSidebarWidth(storageKey, fallbackWidth) {
  if (typeof window === "undefined") {
    return fallbackWidth;
  }

  const width = Number(window.localStorage.getItem(storageKey));

  if (!Number.isFinite(width)) {
    return fallbackWidth;
  }

  return width;
}

export function updateHistory(uri, { replaceHistory = false } = {}) {
  if (typeof window === "undefined" || !isBuildEnvironment()) {
    return;
  }

  const currentContext = getBuilderLocationContext();
  if (currentContext.kind === "none") {
    return;
  }

  const currentUri = getRouteUriFromLocation();
  const nextUrl = getBuilderUrl(currentContext.rootPath, uri, window.location.href);
  const nextPathname = new URL(nextUrl).pathname;

  if (currentUri === uri && currentContext.path === nextPathname) {
    return;
  }

  if (replaceHistory) {
    window.history.replaceState(window.history.state, "", nextUrl);
    return;
  }

  window.history.pushState(window.history.state, "", nextUrl);
}

export function getUriParams(keys) {
  const segments = window.location.pathname.split("/").filter(Boolean);

  return keys.reduce((result, key) => {
    const index = segments.indexOf(key);
    const value = index === -1 ? null : segments[index + 1];

    result[key] = /^\d+$/.test(value || "") ? Number(value) : null;
    return result;
  }, {});
}

export function showsViewUri(showId) {
  return `/features/shows/${showId}/view`;
}

export function seasonViewUri(showId, seasonId) {
  return `/features/shows/${showId}/season/${seasonId}/view`;
}

export function episodeViewUri(showId, episodeId, seasonId = null) {
  if (seasonId) {
    return `/features/shows/${showId}/season/${seasonId}/episode/${episodeId}/view`;
  }

  return `/features/shows/${showId}/episode/${episodeId}/view`;
}

export function showsParentUri(showId, seasonId = null) {
  if (seasonId) {
    return `/features/shows/${showId}/season/${seasonId}/`;
  }

  return `/features/shows/${showId}/`;
}

export function getShowsBackUri(subPage, params) {
  if (!params.shows) {
    return "/features/shows";
  }

  if (subPage.endsWith("/edit")) {
    if (params.episode) {
      return episodeViewUri(params.shows, params.episode, params.season);
    }

    if (params.season) {
      return seasonViewUri(params.shows, params.season);
    }

    return showsViewUri(params.shows);
  }

  if (subPage.endsWith("/episode/create")) {
    return showsParentUri(params.shows, params.season);
  }

  if (subPage.endsWith("/season/create")) {
    return showsParentUri(params.shows);
  }

  if (params.episode) {
    return showsParentUri(params.shows, params.season);
  }

  if (params.season) {
    return showsParentUri(params.shows);
  }

  return "/features/shows";
}

export function eventViewUri(eventId) {
  return `/features/events/${eventId}/view`;
}

export function eventEditUri(eventId) {
  return `/features/events/${eventId}/edit`;
}

export function getEventsBackUri(subPage, params) {
  if (!params.events) {
    return "/features/events";
  }

  if (subPage === "create") {
    return "/features/events";
  }

  if (subPage.endsWith("/edit")) {
    return eventViewUri(params.events);
  }

  return "/features/events";
}
